import { Renderer } from "@unseenco/taxi";
import { isDEVMODE, viewStorage } from "../_globals";
import WknPlyr from "../modules/wkn-plyr";
import WknSwiper from "../modules/wkn-swiper";
import KeyData from "../modules/key-data";

export default class DefaultPageRenderer extends Renderer {
  initialLoad() {
    const onDOMContentLoaded = () => {
      document.removeEventListener("DOMContentLoaded", onDOMContentLoaded);
      this.onEnter();
    };

    document.addEventListener("DOMContentLoaded", onDOMContentLoaded);
  }

  onEnter() {
    this.DOM = {
      plyrContainer: viewStorage.current.querySelector(".plyr__container"),
      swiperCitations: viewStorage.current.querySelector(".swiperCitations"),
      projectLinks: viewStorage.current.querySelectorAll(".project-link"),
      projects: viewStorage.current.querySelectorAll(".project"),
      stickyBar: viewStorage.current.querySelector(".sticky-bar"),
      prevButton: viewStorage.current.querySelector(
        ".sticky-bar--nav-button.--prev"
      ),
      nextButton: viewStorage.current.querySelector(
        ".sticky-bar--nav-button.--next"
      ),
      keyDataSections: viewStorage.current.querySelectorAll(
        ".section--key-datas"
      ),
      pressSwipers: viewStorage.current.querySelectorAll(
        ".section--press .swiper"
      ),
    };

    this.currentProjectIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;

    this.init();
    this.bindEvents();
  }

  init() {
    const {
      plyrContainer = null,
      swiperCitations = null,
      projects = null,
      keyDataSections = null,
      pressSwipers = null,
    } = this.DOM;

    if (plyrContainer) {
      console.log('OurWorkChild - Found video container:', plyrContainer);
      this.plyr = new WknPlyr(plyrContainer);
    } else {
      console.warn('OurWorkChild - No video container found');
    }
    if (swiperCitations) this.mySwiper = new WknSwiper(swiperCitations);

    // Initialize KeyData for each section (one per project)
    if (keyDataSections && keyDataSections.length > 0) {
      console.log(
        "OurWorkChild: Found",
        keyDataSections.length,
        "key data sections"
      );
      this.keyDataModules = Array.from(keyDataSections).map(
        (section) => new KeyData(section)
      );
    }

    if (pressSwipers && pressSwipers.length > 0) {
      this.pressSwiperModules = Array.from(pressSwipers).map(
        (swiperEl) =>
          new WknSwiper(swiperEl, {
            slidesPerView: 1,
            spaceBetween: 0,
            autoHeight: false,
            loop: swiperEl.querySelectorAll(".swiper-slide").length > 1,
            autoplay: {
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            },
          })
      );
    }

    // Show first project by default (and set first link active)
    if (projects && projects.length > 0) {
      this.showProject(0);
    }
  }

  bindEvents() {
    const { projectLinks, projects, stickyBar, prevButton, nextButton } =
      this.DOM;

    if (projectLinks && projects) {
      projectLinks.forEach((link) => {
        link.addEventListener("click", (e) => {
          e.preventDefault();
          const projectIndex = parseInt(
            link.getAttribute("data-project-index")
          );
          this.showProject(projectIndex);
        });
      });
    }

    // Add swipe functionality on mobile
    if (stickyBar && projects && projects.length > 1) {
      stickyBar.addEventListener("touchstart", (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
      });

      stickyBar.addEventListener("touchend", (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe();
      });
    }

    // Add navigation button functionality
    if (prevButton && projects) {
      prevButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.navigateToPrevProject();
      });
    }

    if (nextButton && projects) {
      nextButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.navigateToNextProject();
      });
    }
  }

  navigateToPrevProject() {
    const { projects } = this.DOM;
    if (!projects?.length) return;

    const prevIndex =
      (this.currentProjectIndex - 1 + projects.length) % projects.length;
    this.showProject(prevIndex);
  }

  navigateToNextProject() {
    const { projects } = this.DOM;
    if (!projects?.length) return;

    const nextIndex = (this.currentProjectIndex + 1) % projects.length;
    this.showProject(nextIndex);
  }

  handleSwipe() {
    const { projects } = this.DOM;
    const swipeThreshold = 50; // minimum distance for a swipe

    if (this.touchEndX < this.touchStartX - swipeThreshold) {
      // Swipe left - next project
      const nextIndex = (this.currentProjectIndex + 1) % projects.length;
      this.showProject(nextIndex);
    }

    if (this.touchEndX > this.touchStartX + swipeThreshold) {
      // Swipe right - previous project
      const prevIndex =
        (this.currentProjectIndex - 1 + projects.length) % projects.length;
      this.showProject(prevIndex);
    }
  }

  showProject(index = 0) {
    const { projects, projectLinks } = this.DOM;

    if (!projects?.length) return;

    // clamp index
    index = Math.max(0, Math.min(index, projects.length - 1));

    // Update current index
    this.currentProjectIndex = index;

    // Find current visible project
    const currentProject = Array.from(projects).find((p) =>
      p.classList.contains("--show")
    );

    // Remove active class from all links
    projectLinks.forEach((link) => {
      link.parentElement.classList.remove("--active");
    });

    // Add active class to selected link
    if (projectLinks[index]) {
      projectLinks[index].parentElement.classList.add("--active");
    }

    // Center the active link in the sticky bar on mobile
    this.centerActiveLink(index);

    this.updatePressSwiper(index);

    // If there's a current project and it's different from the new one
    if (currentProject && currentProject !== projects[index]) {
      // Start fade out immediately
      currentProject.classList.remove("--show");
      currentProject.classList.add("--hiding");

      // After 1 second: remove display and show new project
      setTimeout(() => {
        currentProject.classList.remove("--hiding");

        if (projects[index]) {
          // Add show class with opacity 0 first
          projects[index].style.opacity = "0";
          projects[index].classList.add("--show");

          // Force reflow and trigger transition
          void projects[index].offsetWidth;

          // Use requestAnimationFrame to ensure transition happens
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              projects[index].style.opacity = "";
            });
          });
        }
      }, 350);
    } else {
      // No current project or same project, show immediately
      projects.forEach((p) => {
        p.classList.remove("--show", "--hiding");
        p.style.opacity = "";
      });

      if (projects[index]) {
        projects[index].style.opacity = "0";
        projects[index].classList.add("--show");

        void projects[index].offsetWidth;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            projects[index].style.opacity = "";
          });
        });
      }
    }
  }

  updatePressSwiper(projectIndex) {
    if (!this.pressSwiperModules?.length) return;

    this.pressSwiperModules.forEach((module, index) => {
      const { swiper } = module;
      if (!swiper) return;

      if (index === projectIndex) {
        swiper.update();
        swiper.autoplay?.start();
      } else {
        swiper.autoplay?.stop();
      }
    });
  }

  centerActiveLink(index) {
    const { projectLinks } = this.DOM;

    if (!projectLinks || !projectLinks[index]) return;

    const activeLink = projectLinks[index];
    const listItem = activeLink.closest(".joint-actions--item");
    const list = activeLink.closest(".joint-actions--list");

    if (!list || !listItem) return;

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Get the position of the list item within the scrollable container
      const listItemOffsetLeft = listItem.offsetLeft;
      const listItemWidth = listItem.offsetWidth;
      const listWidth = list.offsetWidth;

      // Calculate the scroll position to center the item
      // We want the center of the item to be at the center of the list
      const scrollPosition =
        listItemOffsetLeft - listWidth / 2 + listItemWidth / 2;

      // Smooth scroll to center the active link
      list.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    });
  }

  onLeaveCompleted() {
    this.plyr && this.plyr.destroy();
    this.mySwiper && this.mySwiper.destroy();

    if (this.pressSwiperModules?.length) {
      this.pressSwiperModules.forEach((module) => module.destroy());
    }

    // Destroy all KeyData modules
    if (this.keyDataModules && this.keyDataModules.length > 0) {
      this.keyDataModules.forEach((module) => module.destroy());
    }
  }
}
